<script setup lang="ts">
import { getSurveyGoogleMapKey, loadGoogleMapSdk } from '~/utils/surveyMapSdk'
import { useRtl } from '~/composables/useRtl'
const { t, locale } = useAppI18n()
const isChinese = computed(() => isChineseLocale(locale))
const chinesColon = computed(() => (isChinese.value ? '：' : ': '))
type SurveyTraderInfo = {
  localName?: string
  shortName?: string
  defaultName?: string
  localShortName?: string
  ico?: string
  logo?: string
  annotation?: string
  color?: string
  showName?: string
}

type SurveyMapItem = {
  googleAddr?: string
  googleZoom?: number
  googleImg?: string
  baiduImg?: string
  cover?: string
  images?: string[]
  mapIcon?: string
  country?: string
  surveyor?: string
  wiki_timestamp?: number | string
  evaluate?: string
  evaluatecolor?: string
  vurl?: string
  trader?: SurveyTraderInfo | null
  related?: SurveyTraderInfo | null
  [key: string]: unknown
}

type GoogleNamespace = {
  maps: any
}

type GoogleMapRuntime = {
  map: any
  marker: any
  infoWindow: any
  point: any
}

/** 组件入参 */
const props = defineProps<{
  item?: SurveyMapItem | null
}>()

/** 地图根节点 */
const mapContainerRef = ref<HTMLDivElement | null>(null)

/** 谷歌地图运行时实例 */
const mapRuntime = shallowRef<GoogleMapRuntime | null>(null)

/** 地图 SDK 加载中状态 */
const mapLoading = ref(false)

/** 信息窗显示状态 */
const showInfoWindow = ref(true)

/** 自定义信息窗关闭按钮标识 */
const INFO_WINDOW_CLOSE_ID = 'wikibit-google-map-close'

/** Figma 信息窗底部箭头资源 */
const INFO_WINDOW_ARROW_ASSET =
  'https://resources1.interface003.com/web30/2026/06/15/8516c598-3b34-41aa-9324-a63c1a4a7027.png'

/** Figma 信息窗位置图标资源 */
const INFO_WINDOW_LOCATION_ASSET =
  'https://resources1.interface003.com/web30/2026/06/11/c1e3e37f-9ec4-4e86-a4c1-7a5798d383f1.png'

const DEFAULT_MAP_MAP_ICON =
  'https://resources1.interface003.com/web30/2026/06/15/bc35e40f-b157-4567-acc3-b781f75b4b1a.gif'

const EXPO_MAP_ICON = 'https://resources1.interface003.com/web30/2026/06/23/d1f49e91-aef1-4ff2-8b4a-66ce68bd8d8a.gif'

const mapIcon = computed(() => (props.item?.isexhibition ? EXPO_MAP_ICON : DEFAULT_MAP_MAP_ICON))
/** 信息窗内容宽度 */
const INFO_WINDOW_CONTENT_WIDTH = 280

/** 信息窗箭头宽度 */
const INFO_WINDOW_ARROW_WIDTH = 24

/** 信息窗箭头距离左侧的间距 */
const INFO_WINDOW_ARROW_LEFT_OFFSET = 24

/** 是否为 RTL 布局 */
const { isRtl } = useRtl()

/** 信息窗锚点相对中心的水平偏移量 */
const INFO_WINDOW_PIXEL_OFFSET_X =
  INFO_WINDOW_CONTENT_WIDTH / 2 - INFO_WINDOW_ARROW_LEFT_OFFSET - INFO_WINDOW_ARROW_WIDTH / 2

/** 当前使用的谷歌地图 Key */
const GOOGLE_MAP_KEY = getSurveyGoogleMapKey()

/** 当前初始化批次，用于忽略过期异步回调 */
let googleMapInitId = 0

/** 地图业务数据 */
const mapItem = computed(() => props.item ?? {})

/** 交易商数据 */
const traderInfo = computed(() => mapItem.value.related || mapItem.value.trader || {})

/** 交易商名称 */
const traderName = computed(
  () =>
    traderInfo.value.showName ||
    traderInfo.value.defaultName ||
    traderInfo.value.localName ||
    traderInfo.value.shortName ||
    traderInfo.value.localShortName ||
    '--',
)

/** 交易商图标 */
const traderIcon = computed(() => traderInfo.value.ico || traderInfo.value.logo || '')

/** 实勘标签文案 */
const evaluateText = computed(() => mapItem.value.evaluate || traderInfo.value.annotation || '--')

/** 实勘标签颜色 */
const evaluateColor = computed(() => mapItem.value.evaluatecolor || traderInfo.value.color || '#1f845a')

/** VR 标签显隐 */
const showVrTag = computed(() => Boolean(mapItem.value.vurl))

/** 地图封面图 */
const mapCover = computed(
  () => mapItem.value.googleImg || mapItem.value.baiduImg || mapItem.value.cover || mapItem.value.images?.[0] || '',
)

/** 卡片封面图 */
const cardCover = computed(() => mapItem.value.cover || mapItem.value.images?.[0] || mapCover.value)

/**
 * 解析谷歌地图坐标字符串。
 * @param value `lat,lng` 格式的坐标字符串。
 * @returns 归一化后的经纬度对象，非法值回退为 0。
 */
function parseGoogleAddress(value?: string) {
  const segments = String(value || '').split(',')
  const lat = Number(segments[0])
  const lng = Number(segments[1])

  return {
    lat: Number.isFinite(lat) ? lat : 0,
    lng: Number.isFinite(lng) ? lng : 0,
  }
}

/** 谷歌经纬度 */
const googlePoint = computed(() => parseGoogleAddress(mapItem.value.googleAddr))

/** 是否存在有效坐标 */
const hasValidPoint = computed(() => Boolean(googlePoint.value.lat || googlePoint.value.lng))

/** 地图缩放级别 */
const googleZoom = computed(() => (Number(mapItem.value.googleZoom) > 0 ? Number(mapItem.value.googleZoom) : 15))

/**
 * 转义 HTML 字符，避免信息窗模板字符串被注入。
 * @param value 待转义的文本。
 * @returns 安全的 HTML 字符串。
 */
function escapeHtml(value?: string | number) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

/**
 * 生成谷歌地图信息窗 HTML 内容。
 * @returns 信息窗 HTML 字符串。
 */
function buildInfoWindowContent() {
  /** 交易商图标节点 */
  const iconHtml = traderIcon.value
    ? `<img src="${escapeHtml(traderIcon.value)}" alt="${escapeHtml(traderName.value)}" style="width:20px;height:20px;border-radius:2px;object-fit:cover;flex-shrink:0;" />`
    : ''

  /** VR 标签节点 */
  const vrHtml = showVrTag.value
    ? `<div style="border-radius:2px;background:#185ca0;padding:2px 8px;font-size:11px;line-height:16px;color:#fff;">VR</div>`
    : ''

  /** 封面图节点 */
  const coverHtml = cardCover.value
    ? `<img src="${escapeHtml(cardCover.value)}" alt="${escapeHtml(traderName.value)}" style="aspect-ratio:1200 / 600;width:100%;border-radius:4px;object-fit:cover;" />`
    : ''

  /** 位置图标节点 */
  const locationHtml = `<img src="${escapeHtml(INFO_WINDOW_LOCATION_ASSET)}" alt="" aria-hidden="true" style="width:14px;height:14px;display:block;flex-shrink:0;" />`

  /** 信息窗底部箭头容器样式 */
  const infoWindowArrowContainerStyle = isRtl.value
    ? `display:flex;height:6px;width:100%;align-items:center;justify-content:flex-end;padding-left:${INFO_WINDOW_ARROW_LEFT_OFFSET}px;box-sizing:border-box;`
    : `display:flex;height:6px;width:100%;align-items:center;justify-content:flex-start;padding-left:${INFO_WINDOW_ARROW_LEFT_OFFSET}px;box-sizing:border-box;`

  return `
    <div style="width:${INFO_WINDOW_CONTENT_WIDTH}px;display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 0 0.5px rgba(0,0,0,0.2)) drop-shadow(0 8px 10px rgba(0,0,0,0.12));box-sizing:border-box;">
      <div style="width:100%;display:flex;flex-direction:column;gap:12px;padding:12px;background:#fff;border-radius:4px;box-sizing:border-box;">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;width:100%;">
        <div style="display:flex;min-width:0;align-items:center;gap:8px;">
          ${iconHtml}
          <div style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:16px;line-height:24px;font-weight:500;color:#292a2e;">
            ${escapeHtml(traderName.value)}
          </div>
        </div>
      </div>
      ${coverHtml}
      <div style="display:flex;flex-direction:column;gap:2px;width:100%;">
        <div style="display:flex;flex-wrap:wrap;align-items:center;gap:4px;width:100%;">
          <div style="border-radius:2px;padding:2px 8px;font-size:11px;line-height:16px;color:#fff;background:${escapeHtml(evaluateColor.value)};">
            ${escapeHtml(evaluateText.value)}
          </div>
          ${vrHtml}
          ${locationHtml}
          <span style="font-size:12px;line-height:18px;color:#7d818a;">
            ${escapeHtml(mapItem.value.country || '--')}
          </span>
        </div>
        <div style="font-size:12px;line-height:18px;color:#7d818a;">
          ${t('84279') + chinesColon.value}<span class="ftf" dir="ltr">${escapeHtml(formatTimestamp(mapItem.value.wiki_timestamp))}</span>
        </div>
        <div style="font-size:12px;line-height:18px;color:#7d818a;">
          ${t('84280') + chinesColon.value + escapeHtml(mapItem.value.surveyor || '--')}
        </div>
      </div>
      </div>
      <div style="${infoWindowArrowContainerStyle}">
        <img src="${escapeHtml(INFO_WINDOW_ARROW_ASSET)}" alt="" aria-hidden="true" style="width:${INFO_WINDOW_ARROW_WIDTH}px;height:6px;display:block;" />
      </div>
    </div>
  `
}

/**
 * 生成原生 marker 图标配置。
 * @param googleMaps 当前 Google Maps 命名空间。
 * @returns marker 图标配置对象。
 */
function buildMarkerIcon(googleMaps: any) {
  return {
    url: mapIcon.value,
    scaledSize: new googleMaps.Size(68, 68),
    anchor: new googleMaps.Point(40, 58),
  }
}

/** 打开并刷新当前 marker 对应的信息窗。 */
function openInfoWindow() {
  const runtime = mapRuntime.value

  if (!runtime) {
    return
  }

  runtime.infoWindow.setContent(buildInfoWindowContent())
  runtime.infoWindow.open(runtime.map, runtime.marker)
  showInfoWindow.value = true
}

/** 在信息窗 DOM 渲染完成后绑定内部关闭事件。 */
function bindInfoWindowEvents() {
  const closeElement = document.getElementById(INFO_WINDOW_CLOSE_ID)

  closeElement?.addEventListener('click', () => {
    closeInfoWindow()
  })
}

/** 主动关闭信息窗并同步响应式状态。 */
function closeInfoWindow() {
  const runtime = mapRuntime.value

  if (!runtime) {
    return
  }

  runtime.infoWindow.close()
  showInfoWindow.value = false
}

/** 创建谷歌地图实例、marker 和原生信息窗。 */
function createGoogleMap() {
  if (!mapContainerRef.value || !hasValidPoint.value) {
    return
  }

  const centerPoint = {
    lat: googlePoint.value.lat,
    lng: googlePoint.value.lng,
  }

  /** 地图实例 */
  const googleMaps = (window.google as GoogleNamespace).maps
  const map = new googleMaps.Map(mapContainerRef.value, {
    center: centerPoint,
    zoom: googleZoom.value,
    minZoom: 2,
    maxZoom: googleZoom.value + 4,
    clickableIcons: false,
    zoomControl: true,
    rotateControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
    gestureHandling: 'greedy',
  })

  /** 经纬度点位 */
  const point = new googleMaps.LatLng(googlePoint.value.lat, googlePoint.value.lng)

  /** 原生标记 */
  const marker = new googleMaps.Marker({
    map,
    position: point,
    clickable: true,
    icon: buildMarkerIcon(googleMaps),
  })

  /** 原生信息窗 */
  const infoWindow = new googleMaps.InfoWindow({
    content: buildInfoWindowContent(),
    pixelOffset: new googleMaps.Size(INFO_WINDOW_PIXEL_OFFSET_X, 0),
  })

  mapRuntime.value = {
    map,
    marker,
    infoWindow,
    point,
  }

  marker.addListener('click', () => {
    if (showInfoWindow.value) {
      closeInfoWindow()
      return
    }

    openInfoWindow()
  })

  map.addListener('click', () => {
    closeInfoWindow()
  })

  infoWindow.addListener('closeclick', () => {
    showInfoWindow.value = false
  })

  infoWindow.addListener('domready', () => {
    bindInfoWindowEvents()
  })

  const syncInitialMapView = () => {
    googleMaps.event.trigger(map, 'resize')
    map.setCenter(point)
    map.setZoom(googleZoom.value)
    openInfoWindow()
  }

  googleMaps.event.addListenerOnce(map, 'idle', syncInitialMapView)
  window.setTimeout(syncInitialMapView, 300)
}

/** 异步加载谷歌地图 SDK 并初始化当前地图实例。 */
async function initGoogleMap() {
  if (!hasValidPoint.value) {
    return
  }

  const initId = ++googleMapInitId
  mapLoading.value = true

  try {
    await loadGoogleMapSdk(GOOGLE_MAP_KEY)

    if (initId !== googleMapInitId) {
      return
    }

    await nextTick()
    createGoogleMap()
  } catch (error) {
    if (initId !== googleMapInitId) {
      return
    }

    // eslint-disable-next-line no-console
    console.error('[survey] Google Maps init failed', error)
  } finally {
    if (initId === googleMapInitId) {
      mapLoading.value = false
    }
  }
}

/** 将地图中心点、缩放级别和信息窗重置到默认状态。 */
function resetMapView() {
  const runtime = mapRuntime.value

  if (!runtime) {
    return
  }

  runtime.map.setCenter(runtime.point)
  runtime.map.setZoom(googleZoom.value)
  openInfoWindow()
}

onMounted(() => {
  initGoogleMap()
})

onBeforeUnmount(() => {
  googleMapInitId += 1
  mapRuntime.value?.infoWindow.close()
  mapRuntime.value?.marker.setMap(null)
})

watch(
  () => props.item,
  async () => {
    if (!mapRuntime.value) {
      await initGoogleMap()
      return
    }

    const runtime = mapRuntime.value
    const googleMaps = (window.google as GoogleNamespace).maps
    runtime.point = new googleMaps.LatLng(googlePoint.value.lat, googlePoint.value.lng)
    runtime.marker.setPosition(runtime.point)
    runtime.marker.setIcon(buildMarkerIcon(googleMaps))
    runtime.map.setCenter(runtime.point)
    runtime.map.setZoom(googleZoom.value)
    openInfoWindow()
  },
  { deep: true },
)
</script>

<template>
  <div class="relative w-full">
    <div class="relative aspect-[696/348] w-full overflow-hidden rounded-[12px] bg-[#eef3f7]">
      <CommonImage
        v-if="mapCover && mapLoading"
        :src="mapCover"
        :alt="traderName"
        class="absolute inset-0 z-[2] h-full w-full object-cover"
        loading="lazy"
      />

      <div
        v-if="hasValidPoint"
        ref="mapContainerRef"
        class="absolute inset-0 z-[1] h-full w-full"
        :class="{
          'opacity-0': mapLoading && !mapRuntime,
        }"
      />

      <CommonImage
        v-else-if="mapCover"
        :src="mapCover"
        :alt="traderName"
        class="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
      />

      <button
        v-if="mapRuntime"
        type="button"
        class="absolute top-[16px] right-[16px] z-[3] flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-[999px] hover:bg-[rgba(5,12,31,0.15)] rtl:right-auto rtl:left-[16px]"
        @click="resetMapView"
      >
        <CommonImage
          class="size-full"
          src="https://resources1.interface003.com/web30/2026/06/15/7d09a919-c03e-49a1-b13f-b19879ecff8a.png"
        />
      </button>
    </div>
  </div>
</template>

<style lang="less">
.gm-bundled-control,
.gmnoprint,
.gm-style-cc,
.gm-style-iw-chr,
.gm-style-iw-tc,
a[href*='maps.google.com/maps'],
a[href^='https://maps.google.com/maps'] {
  display: none !important;
}

.gm-style-iw-d {
  overflow: visible !important;
  max-height: none !important;
  padding: 0 !important;
}

.gm-style-iw-c {
  max-width: none !important;
  max-height: none !important;
  padding: 0 !important;
  background: transparent !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  overflow: visible !important;
  height: 260px;
}
</style>
